import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, RefreshCw, Search, Trash2 } from 'lucide-react';
import { DashboardLayout } from '@/layout/DashboardLayout';
import { Button, Input } from '@/design-system';
import { queryKeys } from '@/core/data-fetching/queryKeys';
import {
  COMPANY_DELETION_MODE_LABELS,
  COMPANY_DELETION_STATUS_LABELS,
  companyDeletionsService,
  type CompanyDeletionStatus,
} from './api/company-deletions.service';

const STATUS_STYLES: Record<CompanyDeletionStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  PROCESSING: 'bg-blue-100 text-blue-700 border-blue-200',
  FAILED: 'bg-red-100 text-red-700 border-red-200',
  COMPLETED: 'bg-green-100 text-green-700 border-green-200',
};

const STATUS_FILTERS: Array<{ value: CompanyDeletionStatus | ''; label: string }> = [
  { value: '', label: 'Todos' },
  { value: 'PENDING', label: COMPANY_DELETION_STATUS_LABELS.PENDING },
  { value: 'PROCESSING', label: COMPANY_DELETION_STATUS_LABELS.PROCESSING },
  { value: 'FAILED', label: COMPANY_DELETION_STATUS_LABELS.FAILED },
  { value: 'COMPLETED', label: COMPANY_DELETION_STATUS_LABELS.COMPLETED },
];

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString('pt-BR') : '—');

export function CompanyDeletionsPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<CompanyDeletionStatus | ''>('');
  const filters = { page, limit: 20, search, status: status || undefined };

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: queryKeys.companyDeletions.page(filters),
    queryFn: () => companyDeletionsService.list(filters),
    placeholderData: keepPreviousData,
    // Acompanhamento ao vivo enquanto houver exclusão em andamento.
    refetchInterval: (query) =>
      query.state.data?.data.some((job) => job.status === 'PENDING' || job.status === 'PROCESSING') ? 5_000 : false,
  });

  const jobs = data?.data ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0, totalPages: 1 };

  return (
    <DashboardLayout title="Exclusões de empresas">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-600" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Exclusões de empresas</h1>
              <p className="text-sm text-gray-500">
                Acompanhe as exclusões solicitadas no Manager, processadas em segundo plano.
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching} className="flex items-center gap-1.5">
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Atualizar
          </Button>
        </div>

        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setSearch(searchInput.trim() || undefined);
          }}
        >
          <Input
            placeholder="Buscar por empresa, slug ou e-mail…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            className="max-w-xs"
          />
          <select
            aria-label="Filtrar por status"
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            value={status}
            onChange={(event) => {
              setPage(1);
              setStatus(event.target.value as CompanyDeletionStatus | '');
            }}
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <Button type="submit" size="sm" variant="outline" className="flex items-center gap-1">
            <Search size={14} />
            Buscar
          </Button>
        </form>

        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                {['Empresa', 'Tipo', 'Status', 'Solicitado por', 'Data da exclusão', 'Concluído em', 'Detalhe'].map((heading) => (
                  <th key={heading} className="px-4 py-3 text-left font-medium text-gray-600">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Carregando…</td></tr>
              ) : jobs.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Nenhuma exclusão encontrada.</td></tr>
              ) : jobs.map((job) => (
                <tr key={job.id} className="align-top">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{job.companyName}</p>
                    <p className="text-xs text-gray-500">{job.companySlug}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{COMPANY_DELETION_MODE_LABELS[job.mode]}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[job.status]}`}>
                      {COMPANY_DELETION_STATUS_LABELS[job.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    <p>{job.requestedByDisplayName ?? job.requestedByName ?? '—'}</p>
                    {job.requestedByEmail && <p className="text-xs text-gray-500">{job.requestedByEmail}</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{formatDate(job.requestedAt)}</td>
                  <td className="px-4 py-3 text-gray-700">{job.status === 'COMPLETED' ? formatDate(job.completedAt) : '—'}</td>
                  <td className="max-w-xs px-4 py-3 text-xs text-gray-600">
                    {job.status === 'FAILED'
                      ? <span className="text-red-600">{job.failureReason ?? 'Falha desconhecida.'}</span>
                      : job.attempts > 1 ? `Tentativas: ${job.attempts}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>{pagination.total} registro{pagination.total !== 1 ? 's' : ''} · página {pagination.page} de {pagination.totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="flex items-center gap-1">
                <ChevronLeft size={14} />Anterior
              </Button>
              <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))} className="flex items-center gap-1">
                Próxima<ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
